import React, { useEffect, useState, useCallback } from 'react';
import { Form, Row, Col, Alert } from 'react-bootstrap';
import BaseComparisonModal from './BaseComparisonModal';
import { 
  getStudentsBySchoolId,
  getArchivedAssessmentsWithResults,
  getArchivedAssessments,
  getArchivedAssessmentResults,
  getIndividualStudentResults 
} from "../services/APIClient";

const RepeatingStudentComparisonModal = ({ show, onHide, schoolid }) => {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Academic year selection
  const [leftAcademicYear, setLeftAcademicYear] = useState("");
  const [rightAcademicYear, setRightAcademicYear] = useState("");
  
  // State for available options (to handle async loading)
  const [availableOptions, setAvailableOptions] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState("");
  const [loadingOptions, setLoadingOptions] = useState(false);

  const generateAcademicYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    
    // Generate 10 years back and 2 years forward from current year
    for (let i = currentYear - 10; i <= currentYear + 2; i++) {
      const academicYear = `${i}-${(i + 1).toString().slice(-2)}`;
      const displayYear = `${i}/${(i + 1).toString().slice(-2)}`;
      years.push({
        value: academicYear,
        label: `${displayYear} Academic Year`
      });
    }
    
    return years.reverse(); // Most recent years first
  };

  // Helper function to get grade label from class
  const getGradeLabelFromClass = (classItem) => {
    if (!classItem) return "Unknown Grade";
    if (classItem.grade_level1) return "Junior Infants";
    if (classItem.grade_level2) return "Senior Infants";
    if (classItem.grade_level3) return "1st Class";
    if (classItem.grade_level4) return "2nd Class";
    if (classItem.grade_level5) return "3rd Class";
    if (classItem.grade_level6) return "4th Class";
    if (classItem.grade_level7) return "5th Class";
    if (classItem.grade_level8) return "6th Class";
    return "Unknown Grade";
  };

  const loadRepeatingStudents = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Get all students for the school directly
      const studentsResponse = await getStudentsBySchoolId(schoolid);
      
      // Filter to only show students that have been marked as repeating
      const repeatingStudents = (studentsResponse || []).filter(student => {
        return student.repeatYears && student.repeatYears.length > 0;
      }).map(student => {
        // Get grade info from class if available
        const gradeLabel = student.class ? getGradeLabelFromClass(student.class) : 'Unknown Grade';
        
        return {
          id: student.id,
          name: student.name,
          firstName: student.firstName,
          lastName: student.lastName,
          classId: student.classId,
          className: student.class?.name || 'Unknown Class',
          grade: gradeLabel,
          repeatYears: student.repeatYears.map(entry => entry.year || entry),
          repeatDetails: student.repeatYears || []
        };
      }).sort((a, b) => {
        // Sort alphabetically by display name
        const nameA = (a.firstName && a.lastName) ? `${a.firstName} ${a.lastName}` : a.name || '';
        const nameB = (b.firstName && b.lastName) ? `${b.firstName} ${b.lastName}` : b.name || '';
        return nameA.toLowerCase().localeCompare(nameB.toLowerCase());
      });

      setStudents(repeatingStudents);
    } catch (err) {
      console.error('Error loading repeating students:', err);
      setError('Failed to load repeating students. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [schoolid]);

  const getAvailableOptionsForStudent = async (studentId) => {
    console.log('🔍 DEBUG START: Getting options for student:', studentId);
    console.log('🔍 DEBUG: Left academic year:', leftAcademicYear);
    console.log('🔍 DEBUG: Right academic year:', rightAcademicYear);
    
    if (!leftAcademicYear || !rightAcademicYear) {
      console.log('❌ DEBUG: Missing academic years - returning empty array');
      return [];
    }

    if (!studentId) {
      console.log('❌ DEBUG: Missing student ID - returning empty array');
      return [];
    }

    try {
      // Convert academic year format from "2024-25" to "2024/25" for API calls
      const leftYearForAPI = leftAcademicYear.replace('-', '/');
      
      console.log('📡 DEBUG: Fetching from both data sources...');
      
      // Get archived assessments (repeated year data) for left side
      const archivedAssessmentsPromise = getArchivedAssessments(studentId, leftYearForAPI)
        .then(data => {
          console.log('📡 DEBUG: Archived assessments response:', data);
          return data;
        })
        .catch(err => {
          console.log('❌ DEBUG: Error fetching archived assessments:', err);
          return [];
        });

      // Get current assessments (standard source) for right side
      const currentAssessmentsPromise = getIndividualStudentResults(studentId)
        .then(data => {
          console.log('📡 DEBUG: Current assessments response:', data);
          return data;
        })
        .catch(err => {
          console.log('❌ DEBUG: Error fetching current assessments:', err);
          return [];
        });

      const [archivedAssessments, currentAssessments] = await Promise.all([
        archivedAssessmentsPromise, 
        currentAssessmentsPromise
      ]);

      console.log('📊 DEBUG: Data summary:', {
        archivedCount: archivedAssessments.length,
        currentCount: currentAssessments.length,
        archivedAssessments,
        currentAssessments
      });

      // Extract assessment names from both sources
      const archivedAssessmentNames = new Set();
      const currentAssessmentNames = new Set();
      const allAssessmentNames = new Set();

      // Process archived assessments (these have 'name' and 'type' properties)
      if (archivedAssessments && archivedAssessments.length > 0) {
        archivedAssessments.forEach(assessment => {
          const name = assessment.name || assessment.type;
          if (name) {
            archivedAssessmentNames.add(name);
            allAssessmentNames.add(name);
            console.log('📝 DEBUG: Added archived assessment name:', name);
          }
        });
      }

      // Process current assessments (these have 'name' property directly)
      if (currentAssessments && currentAssessments.length > 0) {
        currentAssessments.forEach(assessment => {
          const name = assessment.name;
          if (name) {
            currentAssessmentNames.add(name);
            allAssessmentNames.add(name);
            console.log('📝 DEBUG: Added current assessment name:', name);
          }
        });
      }
      
      console.log('📋 DEBUG: Assessment names breakdown:', {
        archived: Array.from(archivedAssessmentNames),
        current: Array.from(currentAssessmentNames),
        all: Array.from(allAssessmentNames)
      });

      // Check what assessments exist in both vs only one source
      const commonAssessments = [...archivedAssessmentNames].filter(name => 
        currentAssessmentNames.has(name)
      );
      
      const archivedOnlyAssessments = [...archivedAssessmentNames].filter(name => 
        !currentAssessmentNames.has(name)
      );
      
      const currentOnlyAssessments = [...currentAssessmentNames].filter(name => 
        !archivedAssessmentNames.has(name)
      );

      console.log('🎯 DEBUG: Assessment availability:', {
        commonAssessments,
        archivedOnlyAssessments,
        currentOnlyAssessments,
        totalUnique: allAssessmentNames.size
      });

      // Return ALL unique assessment names (from either source)
      const finalOptions = [...allAssessmentNames].filter(Boolean);
      console.log('✅ DEBUG: Final options returned:', finalOptions);
      console.log('🔍 DEBUG END: Completed processing for student:', studentId);
      
      return finalOptions;

    } catch (error) {
      console.error('💥 DEBUG: Error in getAvailableOptionsForStudent:', error);
      return [];
    }
  };

  const loadAvailableOptions = useCallback(async (studentId) => {
    console.log('🚀 DEBUG: loadAvailableOptions called with:', {
      studentId,
      leftAcademicYear,
      rightAcademicYear,
      studentIdType: typeof studentId
    });
    
    setLoadingOptions(true);
    try {
      console.log('🚀 DEBUG: About to call getAvailableOptionsForStudent...');
      const options = await getAvailableOptionsForStudent(studentId);
      console.log('🚀 DEBUG: getAvailableOptionsForStudent returned:', options);
      setAvailableOptions(options);
      console.log('🚀 DEBUG: Set availableOptions state to:', options);
    } catch (error) {
      console.error('💥 DEBUG: Error in loadAvailableOptions:', error);
      setAvailableOptions([]);
    } finally {
      setLoadingOptions(false);
      console.log('🚀 DEBUG: loadAvailableOptions completed');
    }
  }, [leftAcademicYear, rightAcademicYear]);

  const getData = async (studentId, assessmentPoint) => {
    if (!leftAcademicYear || !rightAcademicYear) {
      return null;
    }

    try {
      // Convert academic year format for API calls
      const leftYearForAPI = leftAcademicYear.replace('-', '/');

      console.log('📊 DEBUG: getData called with:', {
        studentId,
        assessmentPoint,
        leftYearForAPI,
        rightAcademicYear
      });

      // Get archived assessments (repeated year - left side)
      const archivedAssessmentsPromise = getArchivedAssessments(studentId, leftYearForAPI)
        .catch(() => []);

      // Get current assessments (standard source - right side)  
      const currentAssessmentsPromise = getIndividualStudentResults(studentId)
        .catch(() => []);

      const [archivedAssessments, currentAssessments] = await Promise.all([
        archivedAssessmentsPromise,
        currentAssessmentsPromise
      ]);

      console.log('📊 DEBUG: Raw data retrieved:', {
        archivedAssessments,
        currentAssessments
      });

      // Find matching archived assessment
      const archivedAssessmentInfo = archivedAssessments.find(assessment => 
        assessment.name?.includes(assessmentPoint) || 
        assessment.type?.includes(assessmentPoint) ||
        assessmentPoint.toLowerCase().includes(assessment.name?.toLowerCase() || '') ||
        assessmentPoint.toLowerCase().includes(assessment.type?.toLowerCase() || '')
      );

      // Find matching current assessment
      const currentAssessmentInfo = currentAssessments.find(assessment => 
        assessment.name?.includes(assessmentPoint) ||
        assessmentPoint.toLowerCase().includes(assessment.name?.toLowerCase() || '')
      );

      console.log('📊 DEBUG: Matched assessments:', {
        archivedAssessmentInfo,
        currentAssessmentInfo
      });

      // Handle case where assessment doesn't exist in either source
      if (!archivedAssessmentInfo && !currentAssessmentInfo) {
        return {
          error: `No "${assessmentPoint}" assessment found for this student in either archived or current data.`
        };
      }

      // Prepare results object
      const result = {};

      // Handle archived (repeated year) data - LEFT SIDE
      if (archivedAssessmentInfo) {
        try {
          console.log('📊 DEBUG: Fetching archived results for:', archivedAssessmentInfo.assessmentinstanceid);
          const archivedResults = await getArchivedAssessmentResults(studentId, [archivedAssessmentInfo.assessmentinstanceid]);
          const archivedAssessment = archivedResults[0];
          
          if (archivedAssessment) {
            const leftYear = leftAcademicYear.replace('-', '/');
            result.left = {
              date: `${leftYear} - Repeated Year (${new Date(archivedAssessment.dateAssigned).toLocaleDateString()})`,
              results: archivedAssessment.tasks?.map(task => ({
                taskName: task.name,
                score: Math.round((task.score || 0) * 100),
                maxScore: 100,
                timeSpent: task.duration || 0
              })) || [],
              isRepeated: true
            };
            console.log('📊 DEBUG: Left side (archived) data prepared:', result.left);
          }
        } catch (error) {
          console.error('Error fetching archived results:', error);
        }
      }

      // Handle current assessment data - RIGHT SIDE
      if (currentAssessmentInfo) {
        try {
          console.log('📊 DEBUG: Processing current assessment:', currentAssessmentInfo);
          const rightYear = rightAcademicYear.replace('-', '/');
          result.right = {
            date: `${rightYear} - Current Year (${new Date(currentAssessmentInfo.dateAssigned || Date.now()).toLocaleDateString()})`,
            results: currentAssessmentInfo.tasks?.map(task => ({
              taskName: task.name,
              score: Math.round((task.score || 0) * 100),
              maxScore: 100,
              timeSpent: task.duration || 0
            })) || [],
            isCurrent: true
          };
          console.log('📊 DEBUG: Right side (current) data prepared:', result.right);
        } catch (error) {
          console.error('Error processing current assessment:', error);
        }
      }

      // Add placeholders for missing data
      if (!result.left) {
        result.left = {
          date: `${leftAcademicYear.replace('-', '/')} - Repeated Year (No Data)`,
          results: [],
          noData: true,
          isRepeated: true
        };
      }
      
      if (!result.right) {
        result.right = {
          date: `${rightAcademicYear.replace('-', '/')} - Current Year (No Data)`,
          results: [],
          noData: true,
          isCurrent: true
        };
      }

      console.log('📊 DEBUG: Final result object:', result);
      return result;

    } catch (error) {
      console.error('Error fetching comparison data:', error);
      return {
        error: `Failed to load assessment data: ${error.message || 'Unknown error'}`
      };
    }
  };

  const getAvailableOptions = () => {
    // Return the pre-loaded options array
    return availableOptions;
  };

  // Set default academic years
  useEffect(() => {
    if (show && !leftAcademicYear && !rightAcademicYear) {
      const currentYear = new Date().getFullYear();
      const currentMonth = new Date().getMonth();
      
      let currentAcademicYear, previousAcademicYear;
      if (currentMonth >= 8) { // September onwards
        currentAcademicYear = `${currentYear}-${(currentYear + 1).toString().slice(-2)}`;
        previousAcademicYear = `${currentYear - 1}-${currentYear.toString().slice(-2)}`;
      } else { // January to August
        currentAcademicYear = `${currentYear - 1}-${currentYear.toString().slice(-2)}`;
        previousAcademicYear = `${currentYear - 2}-${(currentYear - 1).toString().slice(-2)}`;
      }
      
      setLeftAcademicYear(previousAcademicYear);
      setRightAcademicYear(currentAcademicYear);
    }
  }, [show, leftAcademicYear, rightAcademicYear]);

  // Load students when modal opens or school changes
  useEffect(() => {
    if (show && schoolid) {
      loadRepeatingStudents();
    }
  }, [show, schoolid, loadRepeatingStudents]);

  // Load available options when student or academic years change
  useEffect(() => {
    console.log('🔄 DEBUG: useEffect triggered for loading options:', {
      selectedStudent,
      leftAcademicYear,
      rightAcademicYear,
      shouldLoad: selectedStudent && leftAcademicYear && rightAcademicYear
    });
    
    if (selectedStudent && leftAcademicYear && rightAcademicYear) {
      console.log('✅ DEBUG: All conditions met, calling loadAvailableOptions...');
      loadAvailableOptions(selectedStudent);
    } else {
      console.log('❌ DEBUG: Conditions not met, clearing options');
      setAvailableOptions([]);
    }
  }, [selectedStudent, leftAcademicYear, rightAcademicYear, loadAvailableOptions]);

  // Custom header content for academic year selection
  const renderCustomHeader = () => {
    return (
      <div className="mb-4">
        <Row>
          <Col md={6}>
            <Form.Group>
              <Form.Label className="fw-medium text-primary">
                📚 Repeated Year (Archived Data)
              </Form.Label>
              <Form.Select
                value={leftAcademicYear}
                onChange={(e) => setLeftAcademicYear(e.target.value)}
                size="sm"
              >
                <option value="">Select Repeated Academic Year</option>
                {generateAcademicYearOptions().map(year => (
                  <option key={year.value} value={year.value}>
                    {year.label}
                  </option>
                ))}
              </Form.Select>
              <Form.Text className="text-muted">
                Select the year when the student repeated/was held back
              </Form.Text>
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label className="fw-medium text-success">
                🎯 Current Year (Live Data)
              </Form.Label>
              <Form.Select
                value={rightAcademicYear}
                onChange={(e) => setRightAcademicYear(e.target.value)}
                size="sm"
              >
                <option value="">Select Current Academic Year</option>
                {generateAcademicYearOptions().map(year => (
                  <option key={year.value} value={year.value}>
                    {year.label}
                  </option>
                ))}
              </Form.Select>
              <Form.Text className="text-muted">
                Select the current academic year for comparison
              </Form.Text>
            </Form.Group>
          </Col>
        </Row>
        
        {leftAcademicYear && rightAcademicYear && leftAcademicYear === rightAcademicYear && (
          <Alert variant="warning" className="mt-2 mb-0">
            <small>⚠️ You have selected the same academic year for both sides. Please select different years for meaningful comparison.</small>
          </Alert>
        )}
        
        {leftAcademicYear && rightAcademicYear && leftAcademicYear !== rightAcademicYear && (
          <Alert variant="info" className="mt-2 mb-0">
            <small>
              📊 <strong>Comparison Setup:</strong> 
              Comparing archived data from {leftAcademicYear.replace('-', '/')} (repeated year) 
              with current assessment data from {rightAcademicYear.replace('-', '/')}
            </small>
          </Alert>
        )}
      </div>
    );
  };

  // Custom handler for student selection
  const handleStudentChange = (studentId) => {
    console.log('👤 DEBUG: handleStudentChange called with:', {
      studentId,
      studentIdType: typeof studentId,
      currentSelectedStudent: selectedStudent
    });
    setSelectedStudent(studentId);
    console.log('👤 DEBUG: Updated selectedStudent state to:', studentId);
  };

  // Show loading state in modal
  if (loading) {
    return (
      <BaseComparisonModal
        show={show}
        onHide={onHide}
        title="Repeating Student Comparison"
        type="assessment"
        getData={() => null}
        getAvailableOptions={() => []}
        leftLabel={leftAcademicYear ? `📚 Repeated Year ${leftAcademicYear.replace('-', '/')}` : "📚 Repeated Year"}
        rightLabel={rightAcademicYear ? `🎯 Current Year ${rightAcademicYear.replace('-', '/')}` : "🎯 Current Year"}
        students={[]}
        loading={true}
        loadingMessage="Loading students marked as repeating..."
        customHeader={renderCustomHeader()}
        selectedStudent={selectedStudent}
      />
    );
  }

  // Show error state
  if (error) {
    return (
      <BaseComparisonModal
        show={show}
        onHide={onHide}
        title="Repeating Student Comparison"
        type="assessment"
        getData={() => null}
        getAvailableOptions={() => []}
        leftLabel={leftAcademicYear ? `📚 Repeated Year ${leftAcademicYear.replace('-', '/')}` : "📚 Repeated Year"}
        rightLabel={rightAcademicYear ? `🎯 Current Year ${rightAcademicYear.replace('-', '/')}` : "🎯 Current Year"}
        students={[]}
        error={error}
        customHeader={renderCustomHeader()}
        selectedStudent={selectedStudent}
      />
    );
  }

  // Show empty state if no repeating students found
  if (students.length === 0 && !loading) {
    return (
      <BaseComparisonModal
        show={show}
        onHide={onHide}
        title="Repeating Student Comparison"
        type="assessment"
        getData={() => null}
        getAvailableOptions={() => []}
        leftLabel={leftAcademicYear ? `📚 Repeated Year ${leftAcademicYear.replace('-', '/')}` : "📚 Repeated Year"}
        rightLabel={rightAcademicYear ? `🎯 Current Year ${rightAcademicYear.replace('-', '/')}` : "🎯 Current Year"}
        students={[]}
        emptyMessage="No students found that have been marked as repeating in this school."
        customHeader={renderCustomHeader()}
        selectedStudent={selectedStudent}
      />
    );
  }

  // Enhance student display to show repeat information
  const enhancedStudents = students.map(student => {
    const displayName = student.firstName && student.lastName 
      ? `${student.firstName} ${student.lastName}` 
      : student.name;
    
    const repeatInfo = student.repeatYears.length > 0 
      ? ` (Repeated: ${student.repeatYears.map(year => year.replace('-', '/')).join(', ')})` 
      : '';
    
    return {
      ...student,
      name: `${displayName}${repeatInfo}`,
      displayName: displayName,
      class: `${student.className} (${student.grade})`
    };
  });

  return (
    <BaseComparisonModal
      show={show}
      onHide={onHide}
      title="Repeating Student Comparison"
      type="assessment"
      getData={getData}
      getAvailableOptions={getAvailableOptions}
      leftLabel={leftAcademicYear ? `📚 Repeated Year ${leftAcademicYear.replace('-', '/')}` : "📚 Repeated Year"}
      rightLabel={rightAcademicYear ? `🎯 Current Year ${rightAcademicYear.replace('-', '/')}` : "🎯 Current Year"}
      students={enhancedStudents}
      customHeader={renderCustomHeader()}
      disabled={!leftAcademicYear || !rightAcademicYear || leftAcademicYear === rightAcademicYear}
      onStudentChange={handleStudentChange}
      selectedStudent={selectedStudent}
      loadingOptions={loadingOptions}
    />
  );
};

export default RepeatingStudentComparisonModal;